"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { commonApiFetch } from "@/services/api/common-api";
import { sanitizeErrorForUser } from "@/utils/error-sanitizer";
import type { ProcessedContent } from "./media-utils";
import { buildProcessedContent } from "./media-utils";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface UseDropContentResult {
  drop: ApiDrop | null;
  content: ProcessedContent;
  isLoading: boolean;
  error: unknown;
}

/**
 * Custom hook to fetch and process drop content
 */
export const useDropContent = (dropId, dropPartId, maybeDrop) => {
  const $ = _c(31);
  let t0;
  let t1;
  if ($[0] !== dropId) {
    t0 = [QueryKey.DROP, {
      drop_id: dropId
    }];
    t1 = async () => await commonApiFetch({
      endpoint: `drops/${dropId}`
    });
    $[0] = dropId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = maybeDrop ?? undefined;
  const t3 = !maybeDrop;
  let t4;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t1,
      placeholderData: keepPreviousData,
      initialData: t2,
      enabled: t3
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: drop,
    isFetching,
    error
  } = useQuery(t4);
  let t5;
  if ($[8] !== drop || $[9] !== dropPartId || $[10] !== error || $[11] !== isFetching) {
    bb0: {
      if (isFetching && !drop) {
        let t6;
        if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = {
            segments: [{
              type: "text",
              content: "Loading..."
            }],
            apiMedia: []
          };
          $[13] = t6;
        } else {
          t6 = $[13];
        }
        t5 = t6;
        break bb0;
      }
      if (error) {
        let t6;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = /Drop [0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12} not found/;
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        const regex = t6;
        let t7;
        if ($[15] !== error) {
          const sanitizedError = sanitizeErrorForUser(error);
          t7 = regex.test(sanitizedError) ? "This drop has been deleted by the author" : sanitizedError;
          $[15] = error;
          $[16] = t7;
        } else {
          t7 = $[16];
        }
        const errorMsg = t7;
        let t8;
        if ($[17] !== errorMsg) {
          t8 = [{
            type: "text",
            content: errorMsg
          }];
          $[17] = errorMsg;
          $[18] = t8;
        } else {
          t8 = $[18];
        }
        let t9;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = [];
          $[19] = t9;
        } else {
          t9 = $[19];
        }
        let t10;
        if ($[20] !== t8) {
          t10 = {
            segments: t8,
            apiMedia: t9
          };
          $[20] = t8;
          $[21] = t10;
        } else {
          t10 = $[21];
        }
        t5 = t10;
        break bb0;
      }
      if (!drop) {
        let t6;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = {
            segments: [],
            apiMedia: []
          };
          $[22] = t6;
        } else {
          t6 = $[22];
        }
        t5 = t6;
        break bb0;
      }
      let t6;
      if ($[23] !== dropPartId) {
        t6 = p => p.part_id === dropPartId;
        $[23] = dropPartId;
        $[24] = t6;
      } else {
        t6 = $[24];
      }
      const part = drop.parts.find(t6);
      if (!part) {
        let t7;
        if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = {
            segments: [],
            apiMedia: []
          };
          $[25] = t7;
        } else {
          t7 = $[25];
        }
        t5 = t7;
        break bb0;
      }
      t5 = buildProcessedContent(part.content, part.media);
    }
    $[8] = drop;
    $[9] = dropPartId;
    $[10] = error;
    $[11] = isFetching;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const content = t5;
  const t6 = drop ?? null;
  const t7 = isFetching && !maybeDrop;
  let t8;
  if ($[26] !== content || $[27] !== error || $[28] !== t6 || $[29] !== t7) {
    t8 = {
      drop: t6,
      content,
      isLoading: t7,
      error
    };
    $[26] = content;
    $[27] = error;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
};